import React, { useEffect, useState } from "react";
import "./suggestedTrails.scss";
import { useTranslation } from "react-i18next";
import pendingTrails from "../../../src/api/pendingTrails.json";
import rejectedTrails from "../../../src/api/rejectedTrails.json";
import acceptedTrails from "../../../src/api/acceptedTrails.json";
import emptyTrail from "../../assets/images/emptyTrail.svg";
import useWindowDimension from "../../components/hooks/useWindowDimension";
import { useNavigate } from "react-router-dom";
import imagePlaceholder from "../../assets/images/tottPlaceholder.svg";
import { Image } from "react-bootstrap";
import axios from "axios";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/features/toast/toastSlice";

import "bootstrap/dist/css/bootstrap.css";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { SUGGESTED_TRAILS } from "../../api/endPoint";
import { getCloudinaryFeaturedImageUrl } from "../../util/cloudinaryUpload";
import { format, parseISO } from "date-fns";
import { Helmet } from "react-helmet";

const SuggestedTrails: React.FC = () => {
  const { t } = useTranslation();
  const dimensions = useWindowDimension();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [searchBarHeight, setSearchBarHeight] = useState<number>(0);
  const [filterSectionHeight, setFilterSectionHeight] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>(
    localStorage.getItem("activeTab") || "pending"
  );
  const [pendingActivePage, setPendingActivePage] = useState(1); // Track the active page
  const [rejectedActivePage, setRejectedActivePage] = useState(1); // Track the active page
  const [acceptedActivePage, setAcceptedActivePage] = useState(1); // Track the active page

  const [pendingTrails, setPendingTrails] = useState<any[]>([]);
  const [rejectedTrails, setRejectedTrails] = useState<any[]>([]);
  const [acceptedTrails, setAcceptedTrails] = useState<any[]>([]);

  const [pendingFilteredTrails, setPendingFilteredTrails] =
    useState<any>(pendingTrails);
  const [rejectedFilteredTrails, setRejectedFilteredTrails] =
    useState<any>(rejectedTrails);
  const [acceptedFilteredTrails, setAcceptedFilteredTrails] =
    useState<any>(acceptedTrails);

  // Update localStorage when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    localStorage.setItem("activeTab", tab);
  };

  const handleImageError = (e: any) => {
    e.target.src = imagePlaceholder; // Set the image to placeholder if it fails to load
  };

  useEffect(() => {
    fetchSuggestedTrails();
  }, []);

  const fetchSuggestedTrails = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}${SUGGESTED_TRAILS}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setPendingTrails(response.data.data.pending);
        setRejectedTrails(response.data.data.rejected);
        setAcceptedTrails(response.data.data.accepted);
      }
    } catch (error: any) {
      dispatch(
        showToast({
          show: true,
          message:
            error.response?.data?.message || "Failed to fetch suggested trails",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPendingFilteredTrails(pendingTrails);
    setRejectedFilteredTrails(rejectedTrails);
    setAcceptedFilteredTrails(acceptedTrails);
  }, [pendingTrails, rejectedTrails, acceptedTrails]);

  useEffect(() => {
    const searchBarHeightElement = document.getElementsByClassName(
      "searchSection"
    )[0] as HTMLElement;
    const filterSectionHeightElement = document.getElementsByClassName(
      "filterSection"
    )[0] as HTMLElement;

    if (searchBarHeightElement) {
      setSearchBarHeight(searchBarHeightElement.offsetHeight);
    }
    if (filterSectionHeightElement) {
      setFilterSectionHeight(filterSectionHeightElement.offsetHeight);
    }
  }, []);

  const pendingTrailsPerPage = 20;
  // Calculate the total number of pages
  const totalPendingTrailPages = Math.ceil(
    pendingFilteredTrails?.length / pendingTrailsPerPage
  );
  const currentPendingTrails = pendingFilteredTrails
    ?.sort(
      (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    ) // Sort by latest date first
    .slice(
      (pendingActivePage - 1) * pendingTrailsPerPage,
      pendingActivePage * pendingTrailsPerPage
    );

  const onPendingCardClick = (pendingTrail: any) => {
    navigate(`/suggested-trails/suggested-trail-detail/${pendingTrail.id}`, {
      state: { pendingTrailData: pendingTrail, trailType: "pending" },
    });
  };

  const rejectedTrailsPerPage = 20;
  // Calculate the total number of pages
  const totalRejectedTrailPages = Math.ceil(
    rejectedFilteredTrails?.length / rejectedTrailsPerPage
  );
  const currentRejectedTrails = rejectedFilteredTrails
    ?.sort(
      (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    ) // Sort by latest date first
    .slice(
      (rejectedActivePage - 1) * rejectedTrailsPerPage,
      rejectedActivePage * rejectedTrailsPerPage
    );
  const onRejectedCardClick = (rejectedTrail: any) => {
    navigate(`/suggested-trails/suggested-trail-detail/${rejectedTrail.id}`, {
      state: { rejectedTrailData: rejectedTrail, trailType: "rejected" },
    });
  };

  const acceptedTrailsPerPage = 20;
  // Calculate the total number of pages
  const totalAcceptedTrailPages = Math.ceil(
    acceptedFilteredTrails?.length / acceptedTrailsPerPage
  );
  const currentAcceptedTrails = acceptedFilteredTrails
    ?.sort(
      (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    ) // Sort by latest date first
    .slice(
      (acceptedActivePage - 1) * acceptedTrailsPerPage,
      acceptedActivePage * acceptedTrailsPerPage
    );
  const onAcceptedCardClick = (acceptedTrail: any) => {
    navigate(`/suggested-trails/suggested-trail-detail/${acceptedTrail.id}`, {
      state: { acceptedTrailData: acceptedTrail, trailType: "accepted" },
    });
  };

  const handlePendingPageChange = (pageIndex: number) => {
    setPendingActivePage(pageIndex + 1); // Set the active page in state
    localStorage.setItem("pendingActivePage", (pageIndex + 1).toString()); // Store the active page in localStorage
  };
  const handleRejectedPageChange = (pageIndex: number) => {
    setRejectedActivePage(pageIndex + 1); // Set the active page in state
    localStorage.setItem("rejectedActivePage", (pageIndex + 1).toString()); // Store the active page in localStorage
  };
  const handleAcceptedPageChange = (pageIndex: number) => {
    setAcceptedActivePage(pageIndex + 1); // Set the active page in state
    localStorage.setItem("acceptedActivePage", (pageIndex + 1).toString()); // Store the active page in localStorage
  };

  useEffect(() => {
    const pendingStoredPage = localStorage.getItem("pendingActivePage");
    const rejectedStoredPage = localStorage.getItem("rejectedActivePage");
    const acceptedStoredPage = localStorage.getItem("acceptedActivePage");
    if (pendingStoredPage) {
      setPendingActivePage(parseInt(pendingStoredPage)); // Set the stored active page if available
    }
    if (rejectedStoredPage) {
      setRejectedActivePage(parseInt(rejectedStoredPage)); // Set the stored active page if available
    }
    if (acceptedStoredPage) {
      setAcceptedActivePage(parseInt(acceptedStoredPage)); // Set the stored active page if available
    }
  }, []);

  const ShimmerLoading = () => (
    <div className="mainContent">
      <div className="cardWrapper">
        {[...Array(8)].map((_, index) => (
          <div key={index} className="whiteCard placeholder-glow">
            <div className="cardImg">
              <span className="placeholder w-100"></span>
            </div>
            <div className="cardContent">
              <div className="cardTitlePlaceholder mb-3">
                <span className="placeholder col-8"></span>
                <span className="placeholder col-12"></span>
              </div>
              <div className="contentBtm">
                <div className="dataWrapper">
                  <div className="label">
                    <span className="placeholder col-12"></span>
                  </div>
                  <div className="data">
                    <span className="placeholder col-12"></span>
                  </div>
                </div>
                <div className="dataWrapper">
                  <div className="label">
                    <span className="placeholder col-12"></span>
                  </div>
                  <div className="data">
                    <span className="placeholder col-12"></span>
                  </div>
                </div>
                <div className="dataWrapper">
                  <div className="label">
                    <span className="placeholder col-12"></span>
                  </div>
                  <div className="data">
                    <span className="placeholder col-12"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return "-";

    try {
      // Check if the date string is valid
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return format(parseISO(dateString), "yyyy-MM-dd HH:mm:ss");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };

  return (
    <>
      <Helmet>
        <title>Suggested Trails | Town of 1000 Trails Admin Panel</title>
        <meta
          name="description"
          content="See all user-suggested trails and accept or reject new routes."
        />
      </Helmet>
      <div
        className={
          currentPendingTrails?.length === 0
            ? "main-container container emptyDataContainer tarilListContainer"
            : pendingTrails?.length > 0
            ? "main-container container emptyDataContainer tarilListContainer"
            : "main-container container tarilListContainer"
        }
      >
        <Tabs
          activeKey={activeTab} // Use the state variable
          onSelect={(tab) => tab && handleTabChange(tab)} // Handle tab changes
          id="suggested-tails-tabs"
        >
          <Tab eventKey="pending" title="Pending">
            <div
              className="customScroll"
              style={{
                height:
                  dimensions.width > 1280
                    ? dimensions.height -
                      searchBarHeight -
                      filterSectionHeight -
                      200
                    : dimensions.height -
                      searchBarHeight -
                      filterSectionHeight -
                      250,
                overflow: "auto",
              }}
            >
              {loading ? (
                <ShimmerLoading />
              ) : currentPendingTrails?.length === 0 ? (
                <div className="noDataFont">
                  <div className="emptyImage">
                    <Image src={emptyTrail} alt="No Trail" />
                  </div>
                  <div className="emptyContent">
                    <h5 className="title mb-3">{t("NO_TRAILS_SUGGESTED")}</h5>
                    <div className="description mb-3">
                      {t("CURRENTLY_THERE_ARE_NO_TRAILS_SUGGESTED")}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mainContent">
                    <div className="cardWrapper">
                      {currentPendingTrails?.map((item: any, index: number) =>
                        loading ? (
                          <div
                            key={index}
                            className="whiteCard placeholder-glow"
                          >
                            <div className="cardImg">
                              <span className="placeholder w-100"></span>
                            </div>
                            <div className="cardContent ">
                              <div className="cardTitlePlaceholder mb-3">
                                <span className="placeholder col-8"></span>
                                <span className="placeholder col-12"></span>
                              </div>
                              <div className="contentBtm">
                                <div className="dataWrapper ">
                                  <div className="label">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                  <div className="data">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                </div>
                                <div className="dataWrapper">
                                  <div className="label">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                  <div className="data">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                </div>
                                <div className="dataWrapper">
                                  <div className="label">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                  <div className="data">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => onPendingCardClick(item)}
                            key={index}
                            className={
                              item.status === "PENDING"
                                ? "whiteCard trailCard disable"
                                : "whiteCard trailCard"
                            }
                          >
                            <div className="cardImg">
                              <Image
                                src={
                                  item.featuredImage
                                    ? getCloudinaryFeaturedImageUrl(
                                        item.featuredImage
                                      )
                                    : imagePlaceholder
                                }
                                alt={item.Title || "Trail Image"}
                                onLoad={() => setLoading(false)} // Set loading to false after the image is loaded
                                onError={handleImageError} // Handle image error gracefully
                              />
                            </div>
                            <div className="cardContent">
                              <h6>
                                <span className="badge bg-yellow">
                                  {item.status || "N/A"}
                                </span>
                              </h6>
                              <OverlayTrigger
                                delay={{ hide: 100, show: 100 }}
                                overlay={(props) => (
                                  <Tooltip {...props}>
                                    {item.Title || "No Title"}
                                  </Tooltip>
                                )}
                                placement="bottom"
                              >
                                <div className="cardTitle">
                                  {item.Title || "No Title"}
                                </div>
                              </OverlayTrigger>
                              <div className="contentBtm d-flex flex-column">
                                <span className="mb-1">
                                  {item?.suggestedByName || "N/A"}
                                </span>
                                <span className="ms-2">
                                  {formatDate(item?.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <div
                    className={
                      loading
                        ? "paginationSection placeholder-glow"
                        : "paginationSection"
                    }
                  >
                    {loading ? (
                      <span className="pageNum placeholder col-12"></span>
                    ) : (
                      <span className="pageNum">
                        Showing {currentPendingTrails.length} out of{" "}
                        {pendingTrails.length}
                      </span>
                    )}
                    <nav aria-label="Page navigation">
                      <ul className="pagination">
                        {Array.from({ length: totalPendingTrailPages }).map(
                          (_, pageIndex) => (
                            <li
                              key={pageIndex}
                              className={`page-item ${
                                pendingActivePage === pageIndex + 1
                                  ? "active"
                                  : ""
                              }`}
                              onClick={(e) => {
                                e.preventDefault(); // Prevent default navigation behavior
                                if (!loading) {
                                  handlePendingPageChange(pageIndex);
                                }
                              }}
                            >
                              <span className="page-link">
                                {loading ? (
                                  <span className="placeholder col-10"></span>
                                ) : (
                                  pageIndex + 1
                                )}
                              </span>
                            </li>
                          )
                        )}
                      </ul>
                    </nav>
                  </div>
                </>
              )}
            </div>
          </Tab>
          <Tab eventKey="rejected" title="Rejected">
            <div
              className="customScroll"
              style={{
                height:
                  dimensions.width > 1280
                    ? dimensions.height -
                      searchBarHeight -
                      filterSectionHeight -
                      200
                    : dimensions.height -
                      searchBarHeight -
                      filterSectionHeight -
                      250,
                overflow: "auto",
              }}
            >
              {loading ? (
                <ShimmerLoading />
              ) : currentRejectedTrails?.length === 0 ? (
                <div className="noDataFont">
                  <div className="emptyImage">
                    <Image src={emptyTrail} alt="No Trail" />
                  </div>
                  <div className="emptyContent">
                    <h5 className="title mb-3">
                      {t("NO_TRAILS_FOUND_FOR_REJECTED")}
                    </h5>
                    <div className="description mb-3">
                      {t("CURRENTLY_THERE_ARE_NO_TRAILS_REJECTED")}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mainContent">
                    <div className="cardWrapper">
                      {currentRejectedTrails?.map((item: any, index: number) =>
                        loading ? (
                          <div
                            key={index}
                            className="whiteCard placeholder-glow"
                          >
                            <div className="cardImg">
                              <span className="placeholder w-100"></span>
                            </div>
                            <div className="cardContent ">
                              <div className="cardTitlePlaceholder mb-3">
                                <span className="placeholder col-8"></span>
                                <span className="placeholder col-12"></span>
                              </div>
                              <div className="contentBtm">
                                <div className="dataWrapper ">
                                  <div className="label">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                  <div className="data">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                </div>
                                <div className="dataWrapper">
                                  <div className="label">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                  <div className="data">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                </div>
                                <div className="dataWrapper">
                                  <div className="label">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                  <div className="data">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => onRejectedCardClick(item)}
                            key={index}
                            className={
                              item.status === "REJECTED"
                                ? "whiteCard trailCard disable"
                                : "whiteCard trailCard"
                            }
                          >
                            <div className="cardImg">
                              <Image
                                src={
                                  item.featuredImage
                                    ? getCloudinaryFeaturedImageUrl(
                                        item.featuredImage
                                      )
                                    : imagePlaceholder
                                }
                                alt={item.Title || "Trail Image"}
                                onLoad={() => setLoading(false)} // Set loading to false after the image is loaded
                                onError={handleImageError} // Handle image error gracefully
                              />
                            </div>
                            <div className="cardContent">
                              <h6>
                                <span className="badge bg-red">
                                  {item.status || "N/A"}
                                </span>
                              </h6>
                              <OverlayTrigger
                                delay={{ hide: 100, show: 100 }}
                                overlay={(props) => (
                                  <Tooltip {...props}>
                                    {item.Title || "No Title"}
                                  </Tooltip>
                                )}
                                placement="bottom"
                              >
                                <div className="cardTitle">
                                  {item.Title || "No Title"}
                                </div>
                              </OverlayTrigger>
                              <div className="contentBtm d-flex flex-column">
                                <span className="mb-1">
                                  {item?.suggestedByName || "N/A"}{" "}
                                </span>
                                <span className="ms-2">
                                  {formatDate(item?.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <div
                    className={
                      loading
                        ? "paginationSection placeholder-glow"
                        : "paginationSection"
                    }
                  >
                    {loading ? (
                      <span className="pageNum placeholder col-12"></span>
                    ) : (
                      <span className="pageNum">
                        Showing {currentRejectedTrails.length} out of{" "}
                        {rejectedTrails.length}
                      </span>
                    )}
                    <nav aria-label="Page navigation">
                      <ul className="pagination">
                        {Array.from({ length: totalRejectedTrailPages }).map(
                          (_, pageIndex) => (
                            <li
                              key={pageIndex}
                              className={`page-item ${
                                rejectedActivePage === pageIndex + 1
                                  ? "active"
                                  : ""
                              }`}
                              onClick={(e) => {
                                e.preventDefault(); // Prevent default navigation behavior
                                if (!loading) {
                                  handleRejectedPageChange(pageIndex);
                                }
                              }}
                            >
                              <span className="page-link">
                                {loading ? (
                                  <span className="placeholder col-10"></span>
                                ) : (
                                  pageIndex + 1
                                )}
                              </span>
                            </li>
                          )
                        )}
                      </ul>
                    </nav>
                  </div>
                </>
              )}
            </div>
          </Tab>
          <Tab eventKey="accepted" title="Accepted">
            <div
              className="customScroll"
              style={{
                height:
                  dimensions.width > 1280
                    ? dimensions.height -
                      searchBarHeight -
                      filterSectionHeight -
                      200
                    : dimensions.height -
                      searchBarHeight -
                      filterSectionHeight -
                      250,
                overflow: "auto",
              }}
            >
              {loading ? (
                <ShimmerLoading />
              ) : currentAcceptedTrails?.length === 0 ? (
                <div className="noDataFont">
                  <div className="emptyImage">
                    <Image src={emptyTrail} alt="No Trail" />
                  </div>
                  <div className="emptyContent">
                    <h5 className="title mb-3">
                      {t("NO_TRAILS_FOUND_FOR_ACCEPTED")}
                    </h5>
                    <div className="description mb-3">
                      {t("CURRENTLY_THERE_ARE_NO_TRAILS_ACCEPTED")}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mainContent">
                    <div className="cardWrapper">
                      {currentAcceptedTrails?.map((item: any, index: number) =>
                        loading ? (
                          <div
                            key={index}
                            className="whiteCard placeholder-glow"
                          >
                            <div className="cardImg">
                              <span className="placeholder w-100"></span>
                            </div>
                            <div className="cardContent ">
                              <div className="cardTitlePlaceholder mb-3">
                                <span className="placeholder col-8"></span>
                                <span className="placeholder col-12"></span>
                              </div>
                              <div className="contentBtm">
                                <div className="dataWrapper ">
                                  <div className="label">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                  <div className="data">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                </div>
                                <div className="dataWrapper">
                                  <div className="label">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                  <div className="data">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                </div>
                                <div className="dataWrapper">
                                  <div className="label">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                  <div className="data">
                                    <span className="placeholder col-12"></span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => onAcceptedCardClick(item)}
                            key={index}
                            className={
                              item.status === "ACCEPTED"
                                ? "whiteCard trailCard disable"
                                : "whiteCard trailCard"
                            }
                          >
                            <div className="cardImg">
                              <Image
                                src={
                                  item.featured_image
                                    ? getCloudinaryFeaturedImageUrl(
                                        item.featured_image
                                      )
                                    : imagePlaceholder
                                }
                                alt={item.title || "Trail Image"}
                                onLoad={() => setLoading(false)} // Set loading to false after the image is loaded
                                onError={handleImageError} // Handle image error gracefully
                              />
                            </div>
                            <div className="cardContent">
                              <h6>
                                <span className="badge bg-green">
                                  {item.status || "N/A"}
                                </span>
                              </h6>
                              <OverlayTrigger
                                delay={{ hide: 100, show: 100 }}
                                overlay={(props) => (
                                  <Tooltip {...props}>
                                    {item.title || "No Title"}
                                  </Tooltip>
                                )}
                                placement="bottom"
                              >
                                <div className="cardTitle">
                                  {item.title || "No Title "}
                                </div>
                              </OverlayTrigger>
                              <div className="contentBtm d-flex flex-column">
                                <span className="mb-1">
                                  {item?.suggestedByName || "N/A"}{" "}
                                </span>
                                <span>{item?.created_at || "N/A"} </span>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <div
                    className={
                      loading
                        ? "paginationSection placeholder-glow"
                        : "paginationSection"
                    }
                  >
                    {loading ? (
                      <span className="pageNum placeholder col-12"></span>
                    ) : (
                      <span className="pageNum">
                        Showing {currentAcceptedTrails.length} out of{" "}
                        {acceptedTrails.length}
                      </span>
                    )}
                    <nav aria-label="Page navigation">
                      <ul className="pagination">
                        {Array.from({ length: totalAcceptedTrailPages }).map(
                          (_, pageIndex) => (
                            <li
                              key={pageIndex}
                              className={`page-item ${
                                acceptedActivePage === pageIndex + 1
                                  ? "active"
                                  : ""
                              }`}
                              onClick={(e) => {
                                e.preventDefault(); // Prevent default navigation behavior
                                if (!loading) {
                                  handleAcceptedPageChange(pageIndex);
                                }
                              }}
                            >
                              <span className="page-link">
                                {loading ? (
                                  <span className="placeholder col-10"></span>
                                ) : (
                                  pageIndex + 1
                                )}
                              </span>
                            </li>
                          )
                        )}
                      </ul>
                    </nav>
                  </div>
                </>
              )}
            </div>
          </Tab>
        </Tabs>
      </div>
    </>
  );
};

export default SuggestedTrails;
